// Modules
import ReactModal from "react-modal"
import { ReactNode } from "react"
import { CircularProgress } from "@mui/material"

// Components
import { ModalBottom } from "~styled/modal"
import { ButtonGroup, ButtonPrimary, ButtonSecondary } from "~styled/button"

type ModalProps = {
  title?: string
  isOpen: boolean
  children: ReactNode
  maxWidth?: string
  scrollable?: boolean
  buttonLabel: string
  cancelLabel?: string
  cancelButton?: boolean
  isButtonLoading?: boolean
  isButtonDisabled?: boolean
  onRequestClose: () => void
  onRequestCancel?: () => void
  onRequestConfirm: () => void
  showOptionalButton?: boolean
  optionalButtonLabel?: string
  onOptionalButtonClicked?: () => void
}

const customStyles = (maxWidth: string) => ({
  overlay: {
    backgroundColor: "rgba(0, 0, 0, .35)",
    zIndex: "100",
  },
  content: {
    borderRadius: "10px",
    bottom: "auto",
    left: "50%",
    marginRight: "-50%",
    maxWidth,
    overflow: "visible",
    padding: "0",
    right: "auto",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
  },
})

ReactModal.setAppElement("#root")

const Modal = (props: ModalProps) => {
  // Deconstruct the props
  const {
    title,
    isOpen,
    children,
    maxWidth = "900px",
    scrollable,
    buttonLabel,
    cancelLabel = "Cancel",
    cancelButton,
    isButtonLoading = false,
    isButtonDisabled = false,
    showOptionalButton = false,
    optionalButtonLabel,
    onRequestClose,
    onRequestCancel = onRequestClose,
    onRequestConfirm,
    onOptionalButtonClicked,
  } = props

  return (
    <ReactModal
      style={customStyles(maxWidth)}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
    >
      {/* Modal Container */}
      <div
        style={{
          maxHeight: "80vh",
          overflowY: scrollable ? "auto" : "visible",
          padding: "30px 30px 0 30px",
        }}
      >
        {children}
      </div>

      {/* Modal Bottom */}
      {(title || buttonLabel) && (
        <ModalBottom>
          <span className="title">{title}</span>

          <ButtonGroup>
            {/* Cancel Button */}
            {cancelButton && (
              <ButtonSecondary
                onClick={onRequestCancel}
                disabled={isButtonLoading || isButtonDisabled}
              >
                {cancelLabel}
              </ButtonSecondary>
            )}

            {/* Preview Button */}
            {showOptionalButton && (
              <ButtonSecondary
                onClick={onOptionalButtonClicked}
                disabled={isButtonLoading || isButtonDisabled}
              >
                {optionalButtonLabel}
              </ButtonSecondary>
            )}

            {/* Confirm Button */}
            {buttonLabel && (
              <ButtonPrimary
                onClick={onRequestConfirm}
                disabled={isButtonLoading || isButtonDisabled}
                className={isButtonLoading ? "loading" : ""}
              >
                {!isButtonLoading && buttonLabel}
                {isButtonLoading && (
                  <CircularProgress size={18} className="loading-icon" />
                )}
              </ButtonPrimary>
            )}
          </ButtonGroup>
        </ModalBottom>
      )}
    </ReactModal>
  )
}

export default Modal
